import React, { useEffect, useState } from 'react'
import { X, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Progress } from '@/components/ui/progress'
import { Button } from '@/components/ui/button'

export interface ExportProgressProps {
  isExporting: boolean
  format: string
  progress?: number
  stage?: string
  onCancel?: () => void
  error?: string | null
  onComplete?: () => void
}

/**
 * ExportProgress Component
 *
 * Shows animated progress bar during export with cancellation support.
 * Auto-hides on completion or error.
 */
export const ExportProgress: React.FC<ExportProgressProps> = ({
  isExporting,
  format,
  progress = 0,
  stage = 'Initializing...',
  onCancel,
  error,
  onComplete,
}) => {
  const [visible, setVisible] = useState(false)
  const [currentProgress, setCurrentProgress] = useState(0)
  const [currentStage, setCurrentStage] = useState(stage)

  // Simulate progress if not provided
  useEffect(() => {
    let cleanup: (() => void) | undefined

    if (isExporting) {
      setVisible(true)
      setCurrentStage(stage)

      // If no explicit progress, simulate it
      if (progress === undefined) {
        let simProgress = 0
        const interval = setInterval(() => {
          simProgress += Math.random() * 15
          if (simProgress > 90) {
            clearInterval(interval)
            simProgress = 90
          }
          setCurrentProgress(simProgress)
        }, 500)

        cleanup = () => clearInterval(interval)
      } else {
        setCurrentProgress(progress)
        cleanup = undefined
      }
    } else {
      // Auto-hide after completion
      const timeout = setTimeout(() => {
        setVisible(false)
        if (onComplete) {
          onComplete()
        }
      }, 2000)

      cleanup = () => clearTimeout(timeout)
    }

    return cleanup
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExporting, progress, stage])

  // Update progress when explicitly provided
  useEffect(() => {
    if (progress !== undefined) {
      setCurrentProgress(progress)
    }
  }, [progress])

  // Update stage
  useEffect(() => {
    if (stage) {
      setCurrentStage(stage)
    }
  }, [stage])

  if (!visible && !isExporting) {
    return null
  }

  const formatDisplay = format.toUpperCase()
  const isComplete = currentProgress >= 100
  const hasError = !!error

  return (
    <div
      className={cn(
        'fixed bottom-4 right-4 z-50 w-80',
        'bg-white dark:bg-gray-800',
        'border border-gray-200 dark:border-gray-700',
        'rounded-lg shadow-lg',
        'p-4',
        'transition-all duration-300',
        'animate-in slide-in-from-right-5 fade-in',
        isComplete && !hasError && 'opacity-0'
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          {isExporting ? (
            <Loader2 className="w-5 h-5 text-primary-600 dark:text-primary-400 animate-spin" />
          ) : hasError ? (
            <div className="w-5 h-5 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <X className="w-3 h-3 text-red-600 dark:text-red-400" />
            </div>
          ) : (
            <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {hasError
                ? 'Export Failed'
                : isComplete
                ? 'Export Complete'
                : `Exporting to ${formatDisplay}`}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {hasError
                ? error
                : isComplete
                ? `Your ${formatDisplay} file is ready`
                : currentStage}
            </p>
          </div>
        </div>

        {/* Cancel Button */}
        {isExporting && onCancel && (
          <button
            onClick={onCancel}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            aria-label="Cancel export"
          >
            <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          </button>
        )}
      </div>

      {/* Progress Bar */}
      {(isExporting || (!isExporting && !hasError)) && (
        <div className="space-y-2">
          <Progress
            value={isComplete ? 100 : currentProgress}
            className="h-2"
          />
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>{isComplete ? '100%' : `${Math.round(currentProgress)}%`}</span>
            {!isComplete && (
              <span className="text-xs">Please wait...</span>
            )}
          </div>
        </div>
      )}

      {/* Error Actions */}
      {hasError && onCancel && (
        <div className="mt-3 flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={onCancel}
            className="flex-1"
          >
            Dismiss
          </Button>
        </div>
      )}
    </div>
  )
}

export default ExportProgress
